import { useState } from 'react'
import toast from 'react-hot-toast'
import {CheckCircle2, Loader2, Lock} from 'lucide-react'
import { lumi } from '@/lib/lumi'
import type { CalcInput, TransportMode } from '@/lib/freight'

interface LeadFormProps {
  input: CalcInput
  estimatedRate: number
}

interface FormState {
  fullName: string
  businessEmail: string
  phoneNumber: string
  cargoReadyDate: string
}

const EMPTY: FormState = { fullName: '', businessEmail: '', phoneNumber: '', cargoReadyDate: '' }
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function LeadForm({ input, estimatedRate }: LeadFormProps) {
  const [form, setForm] = useState<FormState>(EMPTY)
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({})
  const [submitting, setSubmitting] = useState(false)
  const [done, setDone] = useState(false)

  const setField = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }))
    setErrors((prev) => ({ ...prev, [key]: undefined }))
  }

  const validate = (): boolean => {
    const next: Partial<Record<keyof FormState, string>> = {}
    if (!form.fullName.trim()) next.fullName = 'Full name is required'
    if (!EMAIL_RE.test(form.businessEmail.trim())) next.businessEmail = 'Enter a valid business email'
    if (!form.phoneNumber.trim()) next.phoneNumber = 'Phone number is required'
    if (!form.cargoReadyDate) next.cargoReadyDate = 'Select a cargo ready date'
    setErrors(next)
    return Object.keys(next).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    setSubmitting(true)
    try {
      const now = new Date().toISOString()
      await lumi.entities.freight_leads.create({
        fullName: form.fullName.trim(),
        businessEmail: form.businessEmail.trim(),
        phoneNumber: form.phoneNumber.trim(),
        cargoReadyDate: new Date(form.cargoReadyDate).toISOString(),
        transportMode: input.mode as TransportMode,
        estimatedRate: Number(estimatedRate),
        origin: input.origin.trim(),
        destination: input.destination.trim(),
        weight: Number(input.weight) || 0,
        weightUnit: input.weightUnit,
        cargoType: input.cargoType,
        creator: form.businessEmail.trim(),
        createdAt: now,
        updatedAt: now,
      })
      setDone(true)
      setForm(EMPTY)
      toast.success('Request received. A logistics specialist will reach out shortly.')
    } catch (err) {
      console.error('Lead submission failed:', err)
      toast.error('Something went wrong submitting your request. Please try again.')
    } finally {
      setSubmitting(false)
    }
  }

  if (done) {
    return (
      <div className="rounded-2xl border border-[#10B981]/30 bg-emerald-50 p-8 text-center">
        <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto" />
        <h3 className="mt-4 text-xl font-bold text-[#0F172A]">Request Submitted</h3>
        <p className="mt-2 text-slate-600">
          Thanks! Our B2B logistics network is preparing firm carrier rates for your shipment.
        </p>
        <button
          onClick={() => setDone(false)}
          className="mt-5 text-sm font-semibold text-[#10B981] hover:underline"
        >
          Submit another request
        </button>
      </div>
    )
  }

  const inputCls =
    'w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[#0F172A] placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#10B981] focus:border-transparent transition'

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 shadow-xl">
      <div className="flex items-center gap-2 text-[#10B981]">
        <Lock className="w-4 h-4" />
        <span className="text-xs font-bold uppercase tracking-wider">Firm Pricing</span>
      </div>
      <h3 className="mt-2 text-xl sm:text-2xl font-bold text-[#0F172A]">
        Unlock Firm Carrier Rates from B2B Logistics Networks
      </h3>
      <p className="mt-1.5 text-sm text-slate-500">
        Share your details and we will match your shipment with vetted carriers.
      </p>

      <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
          <input
            className={inputCls}
            value={form.fullName}
            onChange={(e) => setField('fullName', e.target.value)}
            placeholder="Jordan Meyer"
          />
          {errors.fullName ? <p className="mt-1 text-xs text-red-500">{errors.fullName}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Business Email</label>
          <input
            type="email"
            className={inputCls}
            value={form.businessEmail}
            onChange={(e) => setField('businessEmail', e.target.value)}
            placeholder="you@company.com"
          />
          {errors.businessEmail ? <p className="mt-1 text-xs text-red-500">{errors.businessEmail}</p> : null}
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number</label>
          <input
            type="tel"
            className={inputCls}
            value={form.phoneNumber}
            onChange={(e) => setField('phoneNumber', e.target.value)}
            placeholder="+1 (555) 010-2030"
          />
          {errors.phoneNumber ? <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p> : null}
        </div>

        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Cargo Ready Date</label>
          <input
            type="date"
            className={inputCls}
            value={form.cargoReadyDate}
            onChange={(e) => setField('cargoReadyDate', e.target.value)}
          />
          {errors.cargoReadyDate ? <p className="mt-1 text-xs text-red-500">{errors.cargoReadyDate}</p> : null}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="sm:col-span-2 inline-flex items-center justify-center gap-2 rounded-lg bg-[#10B981] hover:bg-[#0ea472] disabled:opacity-60 text-white font-semibold px-6 py-3 shadow-lg shadow-emerald-900/20 transition-colors"
        >
          {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          {submitting ? 'Submitting...' : 'Unlock Firm Rates'}
        </button>
      </form>
    </div>
  )
}

export default LeadForm
